import { useMemo } from 'react';

import { StatType } from 'services/stats';
import useStats from 'hooks/useStats';

const ProcessDirection = {
  LOAD: 'LOAD',
  UNLOAD: 'UNLOAD',
};

const useProcessDetails = (processId, includeHistory = false) => {
  const processDetails = useStats(StatType.containerDetails, {
    container_id: processId,
    include_history: includeHistory ? 1 : 0,
  }, {
    autoFetch: true,
  });
  const processTimeline = useStats(StatType.timeline, {
    container_id: processId,
  }, {
    autoFetch: true,
    reset: true,
  });

  const process = useMemo(() => {
    let process = null;
    if (processDetails.state.stats.data) {
      process = processDetails.state.stats.data[0] || null;
    }
    return process;
  }, [processDetails.state.stats]);

  const stages = useMemo(() => {
    let stages = {};

    const data = processDetails.state.stats.data;
    if (data && data[0]) {
      stages = {
        docked: { dt: null, isEst: false, tzOffset: null },
        started: { dt: null, isEst: false, tzOffset: null },
        ended: { dt: null, isEst: false, tzOffset: null },
        undocked: { dt: null, isEst: false, tzOffset: null },
      };
      const equipmentDetails = data[0];
      if (equipmentDetails) {
        const {
          docked_at,
          started_at,
          ended_at,
          undocked_at,
          estimated_start,
          estimated_end,
          estimated_undock,
          direction,
          timezone_offset,
        } = equipmentDetails;

        stages.docked.tzOffset = timezone_offset;
        stages.started.tzOffset = timezone_offset;
        stages.ended.tzOffset = timezone_offset;
        stages.undocked.tzOffset = timezone_offset;

        if (docked_at) {
          stages.docked.dt = docked_at;
        }

        if (started_at) {
          stages.started.dt = started_at;
        } else if(estimated_start && direction === ProcessDirection.UNLOAD) {
          stages.started.dt = estimated_start;
          stages.started.isEst = true;
        }

        if (ended_at) {
          stages.ended.dt = ended_at;
        } else if(estimated_end && direction === ProcessDirection.UNLOAD) {
          stages.ended.dt = estimated_end;
          stages.ended.isEst = true;
        }

        if (undocked_at) {
          stages.undocked.dt = undocked_at;
        } else if(estimated_undock && direction === ProcessDirection.UNLOAD) {
          stages.undocked.dt = estimated_undock;
          stages.undocked.isEst = true;
        }
      }
    }
    return stages;
  }, [processDetails.state.stats]);

  const employees = useMemo(() => {
    const employees = processDetails.state.stats.employees || [];
    const equipmentDetails = processDetails.state.stats.data;

    if (equipmentDetails && equipmentDetails[0]) {
      return employees.map(e => {
        e.timezone_offset = equipmentDetails[0].timezone_offset;
        return e;
      });
    }

    return employees;
  }, [processDetails.state.stats]);

  const shifts = useMemo(() => {
    const shifts = processDetails.state.stats.split_shifts || [];
    const equipmentDetails = processDetails.state.stats.data;

    if (equipmentDetails && equipmentDetails[0]) {
      return shifts.map(sh => {
        sh.timezone_offset = equipmentDetails[0].timezone_offset;
        return sh;
      });
    }
    
    return shifts;
  }, [processDetails.state.stats]);

  const timeline = useMemo(() => {
    const timelines = processTimeline.state.stats || [];
    return timelines[0] || null;
  }, [processTimeline.state.stats]);


  return {
    isLoading: processDetails.state.isLoading || processTimeline.state.isLoading,
    processDetails,
    processTimeline,
    stages,
    process,
    employees,
    shifts,
    timeline,
  };
};

export default useProcessDetails;